import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, RefreshControl } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { supabase } from '../../infrastructure/api/supabase';

/**
 * FOUNDER DASHBOARD — real Supabase-backed KPIs.
 * Access is gated in App.tsx to the operator's own account.
 */
export const FounderDashboard = () => {
  const [stats, setStats] = useState({ ttv: 0, netRevenue: 0, activeHomees: 0, totalJobs: 0, activeJobs: 0 });
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    const { data: jobs } = await supabase.from('jobs').select('status, services(price_per_hour)');
    const ttv = (jobs ?? []).reduce((acc: number, job: any) => acc + (job.services?.price_per_hour ?? 0), 0);
    const activeJobs = (jobs ?? []).filter((j: any) => j.status === 'ACTIVE').length;

    const { count: activeHomees } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('role', 'HOMEE')
      .eq('is_active', true);

    setStats({
      ttv,
      netRevenue: ttv * 0.25,
      activeHomees: activeHomees ?? 0,
      totalJobs: (jobs ?? []).length,
      activeJobs,
    });
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  return (
    <ScrollView
      style={styles.container}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#D4AF37" />}
    >
      <LinearGradient colors={['#0A1F44', '#050505']} style={styles.header}>
        <Text style={styles.founderTitle}>FOUNDER VIEW</Text>
        <Text style={styles.subtitle}>HoMee: Real-time Platform State</Text>
      </LinearGradient>

      <View style={styles.statsGrid}>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>CELKOVÝ OBJEM (TTV)</Text>
          <Text style={styles.statValue}>{stats.ttv.toLocaleString('cs-CZ')} Kč</Text>
        </View>
        <View style={[styles.statCard, { borderColor: '#D4AF37', borderWidth: 1 }]}>
          <Text style={[styles.statLabel, { color: '#D4AF37' }]}>ČISTÝ ZISK (25 %)</Text>
          <Text style={[styles.statValue, { color: '#D4AF37' }]}>{stats.netRevenue.toLocaleString('cs-CZ')} Kč</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>AKTIVNÍ ZAKÁZKY</Text>
          <Text style={styles.statValue}>{stats.activeJobs}</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>HOMEES ONLINE</Text>
          <Text style={styles.statValue}>{stats.activeHomees}</Text>
        </View>
        <View style={styles.statCard}>
          <Text style={styles.statLabel}>ZAKÁZKY CELKEM</Text>
          <Text style={styles.statValue}>{stats.totalJobs}</Text>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#050505' },
  header: { padding: 40, paddingTop: 80, borderBottomWidth: 1, borderBottomColor: '#1F1F1F' },
  founderTitle: { color: '#D4AF37', fontSize: 28, fontWeight: '900', letterSpacing: 4 },
  subtitle: { color: '#FFF', fontSize: 14, marginTop: 10, opacity: 0.7 },
  statsGrid: { flexDirection: 'row', flexWrap: 'wrap', padding: 20, gap: 15 },
  statCard: { width: '47%', padding: 20, backgroundColor: '#121212', borderRadius: 8, borderWidth: 0.5, borderColor: '#1F1F1F' },
  statLabel: { color: '#8E9AAF', fontSize: 10, fontWeight: 'bold', letterSpacing: 1 },
  statValue: { color: '#FFF', fontSize: 18, fontWeight: '900', marginTop: 10 },
});
